/**
 * Disparar un seguimiento en Plane.
 *
 * Es **uno solo** para todo el panel. Había dos —el del Overview y el de
 * Clientes— con títulos distintos ("Asignar seguimiento" contra "Disparar un
 * seguimiento en Plane"), validaciones distintas y un toast distinto: el del
 * Overview caía en `planeWorkItemId` cuando no había identificador y le mostraba
 * un UUID crudo a la persona. Lo que de verdad cambia entre pantallas son dos
 * cosas y están acá como props: si el contexto se puede editar antes de crear, y
 * qué consultas hay que invalidar además de las que invalida el hook.
 *
 * El panel no gestiona tareas: las dispara. El estado se administra en Plane, que
 * es la fuente de verdad; acá solo queda la referencia. Si Plane falla el diálogo
 * **no se cierra**, muestra el mensaje que devolvió la API y ofrece Reintentar
 * con lo tipeado intacto — un seguimiento que existe en el panel y no en Plane es
 * un pendiente que nadie va a ver nunca.
 */
import type { EntityType } from '@ops/contracts'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { mensajeDeError } from '../../lib/api'
import { useCreateFollowup, useUsers } from '../../lib/queries'
import { useToast } from '../../shell'
import { Button, Modal, SelectField, Stack, TextField } from '../../ui'

export interface CrearSeguimientoDialogProps {
  open: boolean
  onClose: () => void
  entityType: EntityType
  entityId: string
  /** Título propuesto. Se puede editar antes de crear. */
  tituloInicial: string
  /** Sobre qué es. Va en la bajada del diálogo y en la descripción del work item. */
  contexto: string
  /** Nombre de la entidad, para la bajada: "…en Plane para Armytech". */
  nombre?: string
  /** Dueño preseleccionado, normalmente el de la métrica o el de la cuenta. */
  ownerSugerido?: string | null
  /**
   * Deja editar el texto que va a la descripción del work item. En el Overview
   * el contexto es el detalle del frente y conviene poder afinarlo; en la ficha
   * de un cliente es una línea derivada de la cuenta y no hay nada que editar.
   */
  contextoEditable?: boolean
  /**
   * Consultas a invalidar además de las que invalida `useCreateFollowup`
   * (`followups` y `overview`). La ficha de un cliente lee sus seguimientos de
   * `/api/clients/:id`, así que sin esto la lista de la ficha queda vieja.
   */
  invalidar?: readonly QueryKey[]
}

export function CrearSeguimientoDialog({
  open,
  onClose,
  entityType,
  entityId,
  tituloInicial,
  contexto,
  nombre,
  ownerSugerido,
  contextoEditable = false,
  invalidar,
}: CrearSeguimientoDialogProps) {
  const usuarios = useUsers()
  const crear = useCreateFollowup()
  const toast = useToast()
  const qc = useQueryClient()

  const [titulo, setTitulo] = useState(tituloInicial)
  const [descripcion, setDescripcion] = useState(contexto)
  const [ownerId, setOwnerId] = useState(ownerSugerido ?? '')
  const [fecha, setFecha] = useState('')
  const [errorLocal, setErrorLocal] = useState<string | null>(null)

  // Cada apertura arranca con la sugerencia de esta entidad: si se conservara lo
  // anterior se crearía el work item equivocado sobre la cuenta equivocada.
  useEffect(() => {
    setTitulo(tituloInicial)
    setDescripcion(contexto)
    setOwnerId(ownerSugerido ?? '')
    setFecha('')
    setErrorLocal(null)
    crear.reset()
    // Depende solo de la entidad y su sugerencia a propósito: agregar `crear`
    // volvería a pisar lo que se está escribiendo en cada render de la mutación.
  }, [entityId, entityType, tituloInicial, contexto, ownerSugerido, crear.reset])

  function guardar() {
    if (titulo.trim() === '') {
      setErrorLocal('El seguimiento necesita un título para que se entienda en Plane.')
      return
    }
    if (ownerId === '') {
      setErrorLocal('Elegí un responsable: un seguimiento sin dueño no lo mira nadie.')
      return
    }
    setErrorLocal(null)
    crear.mutate(
      {
        entityType,
        entityId,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        ownerId,
        fechaObjetivo: fecha === '' ? null : fecha,
      },
      {
        onSuccess: (respuesta) => {
          for (const queryKey of invalidar ?? []) {
            void qc.invalidateQueries({ queryKey })
          }
          // Nunca el id crudo: `planeWorkItemId` es un UUID y no le dice nada a
          // nadie. Sin identificador legible, la frase habla del seguimiento.
          const id = respuesta.followup.planeIdentificador ?? 'el seguimiento'
          toast.push('ok', `Se creó ${id} en Plane.`)
          onClose()
        },
      },
    )
  }

  const error =
    errorLocal ?? (crear.error !== null ? mensajeDeError(crear.error) : null)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Disparar un seguimiento en Plane"
      subtitle={
        nombre === undefined
          ? `${contexto} · el estado se gestiona en Plane, no acá`
          : `${nombre} · el estado se gestiona en Plane, no acá`
      }
      error={error}
      onRetry={crear.error !== null ? guardar : undefined}
      reintentando={crear.isPending}
      footer={
        <>
          <Button variante="sutil" onClick={onClose}>
            Cancelar
          </Button>
          <Button variante="primario" onClick={guardar} cargando={crear.isPending}>
            Crear en Plane
          </Button>
        </>
      }
    >
      <Stack gap="sm">
        <TextField
          label="Título del work item"
          requerido
          value={titulo}
          onChange={setTitulo}
          maxLength={200}
        />

        {contextoEditable ? (
          <TextField
            label="Contexto"
            ayuda="Va en la descripción del work item, para que en Plane se entienda sin abrir el panel."
            value={descripcion}
            onChange={setDescripcion}
            filas={3}
            maxLength={2000}
          />
        ) : null}

        <SelectField
          label="Responsable"
          requerido
          value={ownerId}
          onChange={setOwnerId}
          opciones={(usuarios.data ?? []).map((usuario) => ({
            value: usuario.id,
            label: usuario.nombre,
          }))}
          placeholder="Elegí a quién se le asigna"
          {...(usuarios.isError
            ? {
                error: `No pudimos traer el equipo: ${mensajeDeError(usuarios.error)}`,
              }
            : {})}
        />

        <TextField
          label="Fecha objetivo (opcional)"
          value={fecha}
          onChange={setFecha}
          tipo="date"
        />
      </Stack>
    </Modal>
  )
}
